/**
 * Lead status — layer 3 of the patient-stages canon: Active, Hold or Lost.
 *
 * Active by default; Hold and Lost are set by hand, and the status itself is the
 * reason (BAR-2799). Going quiet for 30 days no longer changes the status, it
 * only marks the reason line.
 *
 * Rendered by the PAC list views and the intake patient widget. One
 * implementation on purpose — a second one drifts the first time someone edits
 * either surface.
 */
import dayjs from 'dayjs';
import { Appointment, Flag, Patient, Practitioner, Reference } from '@medplum/fhirtypes';
import {
  BARIENDO_CT_SYSTEM,
  CONSULTATION_APPOINTMENT_CODES,
  FlagCategory,
  PATIENT_FILTER_TAG_SYSTEMS,
} from './constants';

export { LAST_COMMUNICATION_CATEGORIES } from './constants';

export const ACTIVE_LEAD_INACTIVE_DAYS = 30;

/** Dates in reason lines carry the year: these panels are read a year later during win-back. */
const REASON_DATE_FORMAT = 'MMM D, YYYY';

/** A cancelled appointment is not an interaction and never clears an override. */
const IGNORED_APPOINTMENT_STATUSES: Appointment['status'][] = ['cancelled', 'entered-in-error'];

/**
 * Short labels for the reason line. Deliberately a closed map rather than the
 * appointmentType display, which reads "Upcoming initial consultation visit".
 */
const APPOINTMENT_SHORT_LABEL: Record<string, string> = {
  'app-initial-consultation-visit': 'consult',
  'ci-intro-call': 'intro call',
};

/**
 * Every field optional. `isLiveAppointment` already treats a missing status as
 * live, so requiring one here was stricter than the code that consumes it —
 * and callers passing GraphQL selections (which return only what they asked
 * for) could not satisfy it without inventing a status.
 */
export type ActiveLeadAppointment = Partial<Pick<Appointment, 'start' | 'status' | 'appointmentType'>>;

export type LeadStatus = 'active' | 'hold' | 'lost';

export type ParkedLeadStatus = Exclude<LeadStatus, 'active'>;

export const LEAD_STATUSES: readonly LeadStatus[] = ['active', 'hold', 'lost'];

/**
 * The status the filter-tags sync last stamped on the Patient (`derivePatientFilterTags`),
 * for a reader that holds only the Patient. It is `deriveActiveLead` as of the Patient's
 * last tag write, so a Hold cleared by a reply that has not yet re-synced reads as Hold.
 * Undefined when the Patient was never tagged, or carries a value this version does not know.
 */
export function readLeadStatusTag(patient: Pick<Patient, 'meta'>): LeadStatus | undefined {
  const code = patient.meta?.tag?.find((tag) => tag.system === PATIENT_FILTER_TAG_SYSTEMS.status)?.code;
  return LEAD_STATUSES.find((status) => status === code);
}

/** `Flag.code` for each parked status. */
export const LEAD_STATUS_FLAG_CODES: Record<ParkedLeadStatus, { code: string; display: string }> = {
  hold: { code: 'lead-hold', display: 'Hold' },
  lost: { code: 'lead-lost', display: 'Lost' },
};

/** Written before Hold and Lost existed; reads as Lost. */
const LEGACY_INACTIVE_CODE = 'lead-inactive';

export interface ActiveLeadOverride {
  /** `Flag.period.start` — any interaction after this supersedes the override. */
  since: string;
  status: ParkedLeadStatus;
  /** `Flag.author` — renders the avatar on the reason line. */
  author?: Reference<Practitioner>;
}

export interface ActiveLeadInput {
  now: Date;
  lastCommunicationSent?: string;
  appointments?: ActiveLeadAppointment[];
  override?: ActiveLeadOverride;
  /**
   * `EpisodeOfCare.period.start`. The clock's fallback anchor for a lead nobody
   * has contacted yet, so the newest-lead case reads "Signed up · 11 days"
   * rather than "No contact · 11 days" — both true, but only one of them is a
   * failure, and reading neglect on a lead nobody was due to call yet is how a
   * rep stops trusting the column.
   *
   * Not a generic created-at. `RESOURCE_CREATION_DATE_EXTENSION` is re-stamped
   * on every stage change, and history version 1 is the import date for
   * Healthie-migrated patients — wrong for exactly the oldest leads.
   */
  signupAt?: string;
}

export interface ActiveLeadState {
  status: LeadStatus;
  reason: string;
  /** No contact for 30 days or more: the reason line reads amber. */
  reasonWarning: boolean;
  /** Set only while a manual override is live, so callers can render its author. */
  overrideAuthor?: Reference<Practitioner>;
  /** A consult is booked, so the status can't be changed. */
  locked: boolean;
  /** The booked consult's start, while `locked`. */
  lockedConsultStart?: string;
}

function isLiveAppointment(appointment: ActiveLeadAppointment): boolean {
  return !appointment.status || !IGNORED_APPOINTMENT_STATUSES.includes(appointment.status);
}

function appointmentShortLabel(appointment: ActiveLeadAppointment): string {
  const code = appointment.appointmentType?.coding?.find((coding) => coding.code)?.code;
  return (code && APPOINTMENT_SHORT_LABEL[code]) || 'appointment';
}

function isConsult(appointment: ActiveLeadAppointment): boolean {
  return Boolean(
    appointment.appointmentType?.coding?.some(
      (coding) => coding.code && (CONSULTATION_APPOINTMENT_CODES as readonly string[]).includes(coding.code)
    )
  );
}

function pluralDays(days: number): string {
  return days === 1 ? '1 day' : `${days} days`;
}

export function deriveActiveLead(input: ActiveLeadInput): ActiveLeadState {
  const now = dayjs(input.now);
  const appointments = (input.appointments ?? []).filter(isLiveAppointment).filter((a) => a.start);

  const communicationMs = input.lastCommunicationSent ? dayjs(input.lastCommunicationSent).valueOf() : undefined;
  const appointmentMs = appointments.map((a) => dayjs(a.start).valueOf());

  // An override is cleared by any live signal dated after it — including a
  // future appointment, since you cannot be "not interested" with a consult
  // booked. Signup never clears one: it always predates it.
  const override = input.override;
  if (override) {
    const sinceMs = dayjs(override.since).valueOf();
    const signals = communicationMs === undefined ? appointmentMs : [...appointmentMs, communicationMs];

    if (!signals.some((ms) => ms > sinceMs)) {
      return {
        status: override.status,
        reason: `${LEAD_STATUS_FLAG_CODES[override.status].display} · ${dayjs(override.since).format(REASON_DATE_FORMAT)}`,
        reasonWarning: false,
        overrideAuthor: override.author,
        locked: false,
      };
    }
  }

  const upcomingAll = appointments
    .filter((a) => dayjs(a.start).isAfter(now))
    .sort((a, b) => dayjs(a.start).valueOf() - dayjs(b.start).valueOf());
  const upcoming = upcomingAll[0];

  if (upcoming) {
    const bookedConsult = upcomingAll.find(isConsult);
    return {
      status: 'active',
      reason: `Upcoming ${appointmentShortLabel(upcoming)} · ${dayjs(upcoming.start).format(REASON_DATE_FORMAT)}`,
      reasonWarning: false,
      locked: bookedConsult !== undefined,
      ...(bookedConsult ? { lockedConsultStart: bookedConsult.start } : {}),
    };
  }

  const pastMs = appointmentMs.filter((ms) => !dayjs(ms).isAfter(now));
  const interactionMs = [...pastMs, ...(communicationMs === undefined ? [] : [communicationMs])];

  if (interactionMs.length > 0) {
    const days = Math.max(0, now.diff(dayjs(Math.max(...interactionMs)), 'day'));

    return days < ACTIVE_LEAD_INACTIVE_DAYS
      ? { status: 'active', reason: `Last contact · ${pluralDays(days)}`, reasonWarning: false, locked: false }
      : { status: 'active', reason: `No contact · ${pluralDays(days)}`, reasonWarning: true, locked: false };
  }

  // Nothing has happened. The signup date is the only honest anchor, and it
  // keeps its own label in both states because "the clock started at signup and
  // nothing has happened since" stays the more informative sentence.
  if (input.signupAt) {
    const days = Math.max(0, now.diff(dayjs(input.signupAt), 'day'));

    return {
      status: 'active',
      reason: `Signed up · ${pluralDays(days)}`,
      reasonWarning: days >= ACTIVE_LEAD_INACTIVE_DAYS,
      locked: false,
    };
  }

  return { status: 'active', reason: 'No contact', reasonWarning: true, locked: false };
}

/**
 * Reads a PAC lead-status `Flag` into an override.
 *
 * Hold or Lost, from `Flag.code`; a `lead-inactive` Flag from before them reads as Lost.
 *
 * A parked lead is a Flag rather than a Patient extension or a meta.tag
 * because `Flag.author` and `Flag.period.start` carry the who and when that the
 * reason line needs, and neither alternative can without nesting. Precedent:
 * `set-provider-availability-status`.
 *
 * Returns undefined for anything that is not a live lead-status flag, so callers
 * can pass a mixed Flag list straight through.
 *
 * WRITER CONTRACT — this matches on `system` AND `code`, which is stricter than
 * the AccessPolicy criteria that gates the read (a bare token criteria matches
 * any system). So a Flag written with the bare code and no system passes the
 * policy and is then **ignored here**, which fails *open* to Active — the same
 * silent direction as reading no Flag at all. Always construct the category from
 * `PAC_LEAD_STATUS_FLAG_CATEGORY` / `FLAG_CATEGORY_CODING_MAP` rather than
 * hand-writing the code, and keep the round-trip asserted on the write side.
 *
 * The strictness is deliberate, not incidental: a foreign system carrying the
 * same code must not be read as our override, and there is a test for it.
 */
export function parseActiveLeadOverride(flag: Flag): ActiveLeadOverride | undefined {
  if (flag.status !== 'active') {
    return undefined;
  }

  const isLeadStatus = flag.category?.some((category) =>
    category.coding?.some(
      (coding) => coding.system === BARIENDO_CT_SYSTEM && coding.code === FlagCategory.PAC_LEAD_STATUS
    )
  );

  if (!isLeadStatus) {
    return undefined;
  }

  const since = flag.period?.start;
  const code = flag.code?.coding?.find((coding) => coding.system === BARIENDO_CT_SYSTEM)?.code;
  const status: ParkedLeadStatus | undefined =
    code === LEAD_STATUS_FLAG_CODES.hold.code
      ? 'hold'
      : code === LEAD_STATUS_FLAG_CODES.lost.code || code === LEGACY_INACTIVE_CODE
        ? 'lost'
        : undefined;

  if (!since || !status) {
    return undefined;
  }

  return {
    since,
    status,
    author: flag.author as Reference<Practitioner> | undefined,
  };
}
